import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  TextField,
  Checkbox,
} from '@mui/material';
import dayjs from 'dayjs';
import { Close as CloseIcon } from '@mui/icons-material';
import { z } from 'zod';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import TextInput from '../../../component/atoms/TextInput';
import FileUploadInput from '../../../component/atoms/FileUploadInput';
import CustomButton from '../../../component/atoms/CustomButton';
import { createStory, updateStory, editStory as editStoryApi, duplicateStory as duplicateStoryApi } from '../../../redux/apis/distrubutor/storyApis';
import { getProductListBySearch } from '../../../redux/apis/distrubutor/promoApis';
import { countSelectedResults, toggleSelectAllResults } from '../../../utils/productSelection';
import { toast } from 'react-hot-toast';

interface Product {
  Item_Number: number;
  Description: string;
  AltDesc: string;
}

// Zod schema matching the backend Joi validation
const createStorySchema = z.object({
  mediaType: z.enum(['image', 'video'], {
    required_error: 'Media type is required.',
    invalid_type_error: 'Media type must be either "image" or "video".',
  }),
  media: z.string().trim().min(1, 'Media is required.'),
  caption: z.string().trim().optional(),
  items: z.array(z.number()).optional(),
});

type CreateStoryFormData = z.infer<typeof createStorySchema>;

// <input type="datetime-local"> value format (local time, minute precision).
const DT_FMT = 'YYYY-MM-DDTHH:mm';
const STORY_DEFAULT_HOURS = 24;
const defaultWindow = () => ({ start: dayjs().second(0).millisecond(0).format(DT_FMT) });

// Story being edited (from the Stories list). Absent = Add Story.
export interface EditableStory {
  id: number;
  caption?: string | null;
  mediaUrl: string;
  mediaType: string;
  startDate?: string | null;
  expiresAt: string;
  createdAt?: string;
  items?: number[] | null;
}

interface StoryProps {
  open: boolean;
  onClose: () => void;
  storyId?: number; // Add storyId prop for editing/deleting
  editStory?: EditableStory | null;
  /** Story to copy into a brand-new one (fresh id, so fresh analytics). Ignored when editStory is set. */
  duplicateStory?: EditableStory | null;
}

const Story: React.FC<StoryProps> = ({ open, onClose, storyId, editStory, duplicateStory }) => {
  const isEdit = !!editStory;
  const isDuplicate = !isEdit && !!duplicateStory;
  // Story the form is prefilled from (edit or duplicate); its media is kept unless replaced.
  const source = editStory || duplicateStory || null;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [mediaType, setMediaType] = useState<'image' | 'video' | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  // Visibility window: start defaults to now; expiry is always start + 24h (not editable).
  const [startAt, setStartAt] = useState(() => defaultWindow().start);
  const windowError = !startAt || !dayjs(startAt).isValid() ? 'Start date & time is required.' : null;

  // Start value the form opened with (edit mode) — the window is only re-sent if it changed.
  const [initialStartAt, setInitialStartAt] = useState<string | null>(null);

  // On open: Add → fresh default ("now"); Edit → prefill from the story;
  // Duplicate → prefill content from the story but start fresh from "now".
  useEffect(() => {
    if (!open) return;
    if (!source) {
      setStartAt(defaultWindow().start);
      setInitialStartAt(null);
      return;
    }
    if (isDuplicate) {
      setStartAt(defaultWindow().start);
      setInitialStartAt(null);
    } else {
      // startDate is null on stories created before it existed → show createdAt.
      const start = dayjs(source.startDate || source.createdAt || source.expiresAt).format(DT_FMT);
      setStartAt(start);
      setInitialStartAt(start);
    }
    reset({ mediaType: source.mediaType === 'video' ? 'video' : 'image', media: 'existing', caption: source.caption || '', items: source.items || [] });
    setMediaType(source.mediaType === 'video' ? 'video' : 'image');
    const itemNumbers = (source.items || []).map(Number).filter(n => !isNaN(n));
    // Placeholder rows first, then fill in descriptions (best effort).
    setSelectedProducts(itemNumbers.map(n => ({ Item_Number: n, Description: `Item #${n}`, AltDesc: '' })));
    let cancelled = false;
    (async () => {
      const resolved = await Promise.all(itemNumbers.map(async (n) => {
        try {
          const res: any = await getProductListBySearch({ search: String(n) });
          const list: Product[] = res?.data?.data || res?.data || [];
          return list.find(p => Number(p.Item_Number) === n) || null;
        } catch { return null; }
      }));
      if (cancelled) return;
      setSelectedProducts(itemNumbers.map((n, i) => resolved[i] || { Item_Number: n, Description: `Item #${n}`, AltDesc: '' }));
    })();
    return () => { cancelled = true; };
  }, [open, source, isDuplicate]);

  const handleStartChange = (value: string) => setStartAt(value);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (filePreview) {
        URL.revokeObjectURL(filePreview);
      }
    };
  }, [filePreview]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<CreateStoryFormData>({
    resolver: zodResolver(createStorySchema),
    defaultValues: {
      mediaType: 'image',
      media: '',
      caption: '',
      items: [],
    },
  });

  // Debounced product search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (productSearchQuery.length >= 2) {
        searchProducts(productSearchQuery);
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [productSearchQuery]);

  const searchProducts = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const response: any = await getProductListBySearch({ search: query });
      const products = response?.data?.data || response?.data || [];
      setSearchResults(products);
    } catch (err) {
      console.error('Error searching products:', err);
      setSearchResults([]);
      toast.error('Failed to search products');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleProductSelection = (product: Product) => {
    const isSelected = selectedProducts.some(p => p.Item_Number === product.Item_Number);
    const updated = isSelected
      ? selectedProducts.filter(p => p.Item_Number !== product.Item_Number)
      : [...selectedProducts, product];
    setSelectedProducts(updated);
    setValue('items', updated.map(p => Number(p.Item_Number)));
  };

  const handleSelectAllResults = () => {
    const updated = toggleSelectAllResults(selectedProducts, searchResults);
    setSelectedProducts(updated);
    setValue('items', updated.map(p => Number(p.Item_Number)));
  };

  const handleFileUpload = (file: File | null) => {
    // FileUploadInput emits null when the user clears the file
    if (!file) {
      setSelectedFile(null);
      // Edit / duplicate: clearing the new file falls back to the story's current media.
      setMediaType(source ? (source.mediaType === 'video' ? 'video' : 'image') : null);
      if (filePreview) {
        URL.revokeObjectURL(filePreview);
      }
      setFilePreview(null);
      setValue('media', source ? 'existing' : '');
      setError(null);
      return;
    }

    // Check file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setError('File size must be less than 5MB');
      return;
    }

    // Determine media type based on file extension
    const fileExtension = file.name.toLowerCase().split('.').pop();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
    const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'];

    if (imageExtensions.includes(fileExtension || '')) {
      setMediaType('image');
      setValue('mediaType', 'image');
    } else if (videoExtensions.includes(fileExtension || '')) {
      setMediaType('video');
      setValue('mediaType', 'video');
    } else {
      setError('Please upload a valid image');
      return;
    }

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setFilePreview(previewUrl);
    setSelectedFile(file);
    setValue('media', file.name);
    setError(null);
  };

  const resetAfterClose = () => {
    reset();
    setSelectedFile(null);
    setMediaType(null);
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFilePreview(null);
    setError(null);
    setSelectedProducts([]);
    setProductSearchQuery('');
    setSearchResults([]);
  };

  // Edit: send only what the form controls. The window (start + 24h expiry) is re-sent
  // only when the start was changed, so an untouched story keeps its stored dates.
  const submitEdit = async (data: CreateStoryFormData) => {
    if (!editStory) return;
    if (windowError) { setError(windowError); return; }
    setIsSubmitting(true);
    setError(null);
    try {
      const startChanged = startAt !== initialStartAt;
      await editStoryApi(editStory.id.toString(), {
        caption: data.caption || '',
        items: selectedProducts.map(p => Number(p.Item_Number)),
        ...(startChanged ? {
          startDate: dayjs(startAt).toISOString(),
          expiresAt: dayjs(startAt).add(STORY_DEFAULT_HOURS, 'hour').toISOString(),
        } : {}),
        media: selectedFile,
        mediaType: mediaType || undefined,
      });
      toast.success('Story updated successfully');
      onClose();
      resetAfterClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to update story';
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Duplicate: a new story from the source's media (unless replaced), with this form's
  // caption, products and a fresh window. The source story is left untouched.
  const submitDuplicate = async (data: CreateStoryFormData) => {
    if (!duplicateStory) return;
    if (windowError) { setError(windowError); return; }
    setIsSubmitting(true);
    setError(null);
    try {
      await duplicateStoryApi(duplicateStory.id.toString(), {
        caption: data.caption || '',
        items: selectedProducts.map(p => Number(p.Item_Number)),
        startDate: dayjs(startAt).toISOString(),
        expiresAt: dayjs(startAt).add(STORY_DEFAULT_HOURS, 'hour').toISOString(),
        media: selectedFile,
        mediaType: mediaType || undefined,
      });
      toast.success('Story duplicated successfully');
      onClose();
      resetAfterClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to duplicate story';
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = async (data: CreateStoryFormData) => {
    if (isEdit) return submitEdit(data);
    if (isDuplicate) return submitDuplicate(data);
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }
    if (windowError) {
      setError(windowError);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Here you would typically upload the file to your backend
      // For now, we'll just simulate the API call
     const sendObject = {
      mediaType: data.mediaType,
      media: selectedFile,
      caption: data.caption || '',
      items: selectedProducts.map(p => Number(p.Item_Number)),
      startDate: dayjs(startAt).toISOString(),
      expiresAt: dayjs(startAt).add(STORY_DEFAULT_HOURS, 'hour').toISOString(),
     }
     try{
         await createStory(sendObject);
        toast.success('Story created successfully');
     }catch(err:any){
        toast.error(err.response.data.message || 'Failed to create story');
     }

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Success - close modal and reset form
      onClose();
      reset();
      setSelectedFile(null);
      setMediaType(null);
      setSelectedProducts([]);
      setProductSearchQuery('');
      setSearchResults([]);
      
      // You can add success toast here
      // console.log('Story created successfully:', data);
      
    } catch (err) {
      setError('Failed to create story. Please try again.');
      console.error('Error creating story:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      resetAfterClose();
    }
  };

  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!storyId) return;
    
    setIsDeleting(true);
    try {
      await updateStory(storyId.toString(), { isActive: false });
      toast.success('Story deleted successfully');
      setDeleteConfirmOpen(false);
      onClose(); // Close the modal after successful deletion
    } catch (error: any) {
      console.error('Error deleting story:', error);
      toast.error(error.response?.data?.message || 'Failed to delete story');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: 400,
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" fontWeight={600}>
          {isEdit ? 'Edit Story' : isDuplicate ? 'Duplicate Story' : 'Add Story'}
        </Typography>
        <IconButton
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ pt: 0 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Caption Field */}
            <Box>
              <Typography variant="subtitle2" fontWeight={500} sx={{ mb: 1 }}>
                Caption
              </Typography>
              <Controller
                name="caption"
                control={control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    placeholder="Enter your story caption (optional)"
                    multiline
                    rows={3}
                    fullWidth
                    error={!!errors.caption}
                    helperText={errors.caption?.message}
                    disabled={isSubmitting}
                  />
                )}
              />
            </Box>

            {/* Visibility window: retailers see the story only between these two */}
            <Box>
              <Typography variant="subtitle2" fontWeight={500} sx={{ mb: 1 }}>
                Story Visibility
              </Typography>
              {/* Expire is not editable here: it is always start + 24h (sent on submit). */}
              <TextField
                label="Start Date & Time"
                type="datetime-local"
                size="small"
                fullWidth
                value={startAt}
                onChange={(e) => handleStartChange(e.target.value)}
                disabled={isSubmitting}
                InputLabelProps={{ shrink: true }}
                error={!!windowError}
              />
              <Typography variant="caption" color={windowError ? 'error' : 'text.secondary'} sx={{ mt: 0.75, display: 'block' }}>
                {windowError || 'Retailers see this story for 24 hours from the start time.'}
              </Typography>
            </Box>

            {/* File Upload Field */}
            <Box>
              <Typography variant="subtitle2" fontWeight={500} sx={{ mb: 1 }}>
                {source ? 'Media File' : 'Media File *'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                {source
                  ? 'Choose a new image (max 5MB) only to replace the current one'
                  : 'Upload an image (max 5MB)'}
              </Typography>

              {/* Edit / duplicate: current media, shown until a replacement is picked */}
              {source && !filePreview && source.mediaUrl && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Current media
                  </Typography>
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', display: 'flex', justifyContent: 'center', bgcolor: 'background.default' }}>
                    {source.mediaType === 'video' ? (
                      <video src={source.mediaUrl} controls style={{ maxWidth: '80%', maxHeight: 200 }} />
                    ) : (
                      <img src={source.mediaUrl} alt="Current story media" style={{ maxWidth: '80%', maxHeight: 200, objectFit: 'contain' }} />
                    )}
                  </Box>
                </Box>
              )}
              
                             <Controller
                 name="media"
                 control={control}
                 render={({  }) => (
                   <FileUploadInput
                     label="Media File"
                     onChange={(file) => handleFileUpload((file as File | null) ?? null)}
                     error={!!errors.media}
                     helperText={errors.media?.message || "Upload an image"}
                     accept="image/*,video/*"
                   />
                 )}
               />

               {/* File Preview */}
               {filePreview && (
                 <Box sx={{ mt: 2 }}>
                   <Typography variant="subtitle2" fontWeight={500} sx={{ mb: 1 }}>
                     Preview
                   </Typography>
                   <Box
                     sx={{
                       border: '1px solid',
                       borderColor: 'divider',
                       borderRadius: 1,
                       overflow: 'hidden',
                       maxHeight: 300,
                       display: 'flex',
                       justifyContent: 'center',
                       alignItems: 'center',
                       bgcolor: 'background.default'
                     }}
                   >
                     {mediaType === 'image' ? (
                       <img
                         src={filePreview}
                         alt="Preview"
                         style={{
                           maxWidth: '80%',
                           maxHeight: '200px',
                           objectFit: 'contain'
                         }}
                       />
                     ) : mediaType === 'video' ? (
                       <video
                         src={filePreview}
                         controls
                         style={{
                           maxWidth: '80%',
                           maxHeight: '200px'
                         }}
                       />
                     ) : null}
                   </Box>
                   <Box sx={{ mt: 1, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                     <Typography variant="body2" color="text.secondary">
                       Selected: {selectedFile?.name} ({(selectedFile?.size || 0 / 1024 / 1024).toFixed(2)} MB)
                     </Typography>
                     <Typography variant="caption" color="primary.main">
                       Type: {mediaType}
                     </Typography>
                   </Box>
                 </Box>
               )}
            </Box>

            {/* Items Search */}
            <Box>
              <Typography variant="subtitle2" fontWeight={500} sx={{ mb: 1 }}>
                Items (optional)
              </Typography>
              <TextInput
                label="Search products by name or description"
                value={productSearchQuery}
                onChange={(e: any) => setProductSearchQuery(e.target.value)}
                placeholder="Type to search products..."
                helperText="Enter at least 2 characters to search. Selected items will be sent with the story."
                fullWidth
              />

              {searchLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : searchResults.length > 0 ? (
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Search Results ({searchResults.length})
                    </Typography>
                    {(() => {
                      const selectedCount = countSelectedResults(selectedProducts, searchResults);
                      return (
                        <Box
                          component="label"
                          sx={{ display: 'flex', alignItems: 'center', cursor: isSubmitting ? 'default' : 'pointer', userSelect: 'none' }}
                        >
                          <Checkbox
                            size="small"
                            checked={selectedCount === searchResults.length}
                            indeterminate={selectedCount > 0 && selectedCount < searchResults.length}
                            onChange={handleSelectAllResults}
                            disabled={isSubmitting}
                            sx={{ p: 0.5 }}
                          />
                          <Typography variant="caption" fontWeight={500}>
                            Select All
                          </Typography>
                        </Box>
                      );
                    })()}
                  </Box>
                  <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    {searchResults.map((product) => {
                      const isSelected = selectedProducts.some(p => p.Item_Number === product.Item_Number);
                      return (
                        <Box
                          key={product.Item_Number}
                          onClick={() => handleProductSelection(product)}
                          sx={{
                            p: 1.5,
                            borderBottom: '1px solid #f0f0f0',
                            cursor: 'pointer',
                            backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                            '&:hover': {
                              backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0,0,0,0.04)',
                            },
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: 18,
                              height: 18,
                              borderRadius: '50%',
                              border: '2px solid',
                              borderColor: isSelected ? 'primary.main' : '#ccc',
                              backgroundColor: isSelected ? 'primary.main' : 'transparent',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {isSelected && (
                              <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'white' }} />
                            )}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={500}>
                              {product.Description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Item: {product.Item_Number} | Alt: {product.AltDesc}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ) : productSearchQuery.length >= 2 && !searchLoading ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 1 }}>
                  No products found for "{productSearchQuery}"
                </Typography>
              ) : null}

              {selectedProducts.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.primary" sx={{ mb: 0.5, display: 'block' }}>
                    Selected Items ({selectedProducts.length})
                  </Typography>
                  <Box sx={{ maxHeight: 150, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    {selectedProducts.map((product) => (
                      <Box
                        key={product.Item_Number}
                        sx={{
                          p: 1.5,
                          borderBottom: '1px solid #f0f0f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          backgroundColor: 'rgba(25, 118, 210, 0.06)',
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {product.Description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Item: {product.Item_Number}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleProductSelection(product)}
                          sx={{ color: 'error.main' }}
                          aria-label="Remove item"
                        >
                          <Box component="span" sx={{ fontSize: 18, fontWeight: 'bold', lineHeight: 1 }}>
                            ×
                          </Box>
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          {/* Delete Button - Only show if storyId exists (editing mode) */}
          {storyId && (
            <Button
              onClick={handleDeleteClick}
              disabled={isSubmitting || isDeleting}
              variant="outlined"
              color="error"
              sx={{ borderRadius: 2, mr: 'auto' }}
            >
              Delete
            </Button>
          )}
          
            <CustomButton
              onClick={handleClose}
              disabled={isSubmitting || isDeleting}
              buttonType="primary"
              appearance="outlined"
              sx={{ borderRadius: 2 }}
              fullWidth={false}
              
            >
              Cancel
          </CustomButton>
          <CustomButton
            type="submit"
            buttonType="primary"
            appearance="filled"
            loading={isSubmitting}
            disabled={(!source && !selectedFile) || !!windowError}
            fullWidth={false}
          >
            {isEdit
              ? (isSubmitting ? 'Updating...' : 'Update Story')
              : isDuplicate
                ? (isSubmitting ? 'Creating...' : 'Create Copy')
                : (isSubmitting ? 'Creating...' : 'Create Story')}
          </CustomButton>
                 </DialogActions>
       </form>

       {/* Delete Confirmation Dialog */}
       <Dialog
         open={deleteConfirmOpen}
         onClose={handleDeleteCancel}
         maxWidth="xs"
         fullWidth
         PaperProps={{
           sx: {
             borderRadius: 2,
           }
         }}
       >
         <DialogTitle sx={{ pb: 1 }}>
           <Typography variant="h6" fontWeight={600} color="error">
             Delete Story
           </Typography>
         </DialogTitle>
         
         <DialogContent sx={{ pt: 0 }}>
           <Typography sx={{ mb: 2 }}>
             Are you sure you want to delete this story? This action cannot be undone.
           </Typography>
         </DialogContent>
         
         <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
           <Button
             onClick={handleDeleteCancel}
             disabled={isDeleting}
             variant="outlined"
             sx={{ borderRadius: 2 }}
           >
             Cancel
           </Button>
           <Button
             onClick={handleDeleteConfirm}
             disabled={isDeleting}
             variant="contained"
             color="error"
             sx={{ borderRadius: 2 }}
             startIcon={isDeleting ? <CircularProgress size={16} /> : null}
           >
             {isDeleting ? 'Deleting...' : 'Delete'}
           </Button>
         </DialogActions>
       </Dialog>
     </Dialog>
   );
 };

export default Story;
